import axios from "axios";
import appConfig from "config";
import { store } from "index";

let isRefreshing = false;
let isTokenRefreshPending: Promise<void> | null = null;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const axiosInstance = axios.create({
  baseURL: appConfig.api.API,
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  async (config) => {
    if (isTokenRefreshPending) {
      try {
        await isTokenRefreshPending;
      } catch (err) {
        return Promise.reject(err);
      }
    }

    const authUser = sessionStorage.getItem("authUser");
    const token = authUser ? JSON.parse(authUser).token : null;

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    const state = store.getState();
    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const isRefreshUrl = originalRequest.url?.includes("/adminauth/refreshtoken");
    const authUser = sessionStorage.getItem("authUser");
    const refresh_token = authUser ? JSON.parse(authUser).refresh_token : null;
    if (status === 401 && !originalRequest._retry && !isRefreshUrl) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      isTokenRefreshPending = new Promise(async (resolve, reject) => {
        try {
          const state = store.getState();
          // const clientId = state.Client.client_id;
          const refreshResponse = await axios.post(
            appConfig.api.API + "/adminauth/refreshtoken",
            {refresh_token},
            {
              withCredentials: true, headers: {
                // "client-id": clientId,
                "Content-Type": "application/json",
              }
            }
          );

          const newAccessToken = refreshResponse.data?.data;
          if (!newAccessToken) throw new Error("No access token returned");

          const authUser = sessionStorage.getItem("authUser");
          if (authUser) {
            const parsed = JSON.parse(authUser);
            parsed.token = newAccessToken;
            sessionStorage.setItem("authUser", JSON.stringify(parsed));
          }

          axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          resolve();
        } catch (err) {
          processQueue(err, null);

          const axiosError = err as any;
          const refreshStatus = axiosError?.response?.status;

          const isRefreshCall = originalRequest.url?.includes("/adminauth/refreshtoken");

          if (isRefreshCall || [401, 403, 404, 500, 400].includes(refreshStatus)) {
            console.warn("Refresh token failed:", refreshStatus);
            sessionStorage.removeItem("authUser");
            window.location.href = "/login";
            reject(err);
            return;
          }

          reject(err);
        } finally {
          isRefreshing = false;
          isTokenRefreshPending = null;
        }
      });

      try {
        await isTokenRefreshPending;
        return axiosInstance(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    // Still failing after refresh attempt — force logout
    if (status === 401 && originalRequest._retry) {
      sessionStorage.removeItem("authUser");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

